<template>
	<div class="purchase-summary">
		<h2 class="purchase-summary__title">
			{{ summaryTitle }}
		</h2>

		<LineItem
			label="Tokens you already own"
			:unit="tokensForThisExport.unit"
			:value="props.tokensOwned" />
		<LineItem
			v-if="paidPackageSelected"
			label="Tokens after purchase"
			:unit="tokensAfterPurchase.unit"
			:value="tokensAfterPurchase.value" />
		<LineItem
			label="Tokens for this export"
			:unit="tokensForThisExport.unit"
			:value="tokensForThisExport.value" />
		<LineItem
			label="Tokens remaining after export"
			:unit="remainingTokensAfterExport.unit"
			:value="remainingTokensAfterExport.value" />

		<hr
			v-if="paidPackageSelected"
			class="purchase-summary__divider" />

		<LineItem
			v-if="paidPackageSelected"
			label="Tokens for purchase"
			total
			:unit="selectedPackage.unit"
			:value="selectedPackage.value" />
		<LineItem
			v-if="paidPackageSelected"
			label="Price per token"
			:unit="pricePerToken.unit"
			:value="pricePerToken.value" />

		<LineItem
			v-if="paidPackageSelected || isFree"
			label="Total price"
			total
			:unit="totalPrice.unit"
			:value="totalPrice.value" />

		<Button
			v-if="paidPackageSelected"
			class="purchase-summary__button"
			icon="far fa-credit-card"
			label="Buy Tokens and Export"
			:loading="props.checkoutLoading"
			@click="emit('checkout')" />
		<Button
			v-else-if="!isFree"
			class="purchase-summary__button"
			label="Use Tokens and Export"
			:icon="`far ${exportType === 'zip' ? 'fa-download' : 'fa-cloud-arrow-up'}`"
			:loading="props.checkoutLoading"
			@click="emit('export')" />
		<Button
			v-else
			class="purchase-summary__button"
			label="Export and Deliver"
			:icon="`far ${exportType === 'zip' ? 'fa-download' : 'fa-cloud-arrow-up'}`"
			:loading="props.checkoutLoading"
			@click="emit('export')" />

		<div
			v-if="paidPackageSelected"
			class="purchase-summary__secure">
			Secure payment processed by Stripe
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Button from 'primevue/button'
import LineItem from './PurchaseSummary/LineItem.vue'

type LineItemProps = {
	value: string | number
	unit: 'tok' | '€' | null
}

const props = defineProps<{
	selected: { tokens: number; price: number }
	tokensNeeded: number
	tokensOwned: number
	exportType: 'git' | 'zip'
	checkoutLoading: boolean
}>()

const emit = defineEmits<{
	(e: 'checkout'): void
	(e: 'export'): void
}>()

const summaryTitle = computed(() => {
	if (props.selected.tokens > 0) return 'Purchase Summary'
	return 'Export Summary'
})

const isFree = computed(() => props.tokensNeeded <= 5 && !paidPackageSelected.value)
const paidPackageSelected = computed(() => props.selected.tokens > 0)

const selectedPackage = computed<LineItemProps>(() => {
	if (isFree.value)
		return {
			value: 'Free',
			unit: null,
		}
	return {
		value: props.selected.tokens,
		unit: 'tok',
	}
})
const pricePerToken = computed<LineItemProps>(() => ({
	value: props.selected.price / props.selected.tokens,
	unit: '€',
}))
const tokensAfterPurchase = computed<LineItemProps>(() => ({
	value: props.tokensOwned + props.selected.tokens,
	unit: 'tok',
}))
const tokensForThisExport = computed<LineItemProps>(() => {
	if (isFree.value)
		return {
			value: 0,
			unit: 'tok',
		}
	return {
		value: props.tokensNeeded,
		unit: 'tok',
	}
})
const remainingTokensAfterExport = computed<LineItemProps>(() => {
	if (isFree.value)
		return {
			value: props.tokensOwned + props.selected.tokens,
			unit: 'tok',
		}
	return {
		value: props.tokensOwned + props.selected.tokens - props.tokensNeeded,
		unit: 'tok',
	}
})
const totalPrice = computed<LineItemProps>(() => {
	if (isFree.value)
		return {
			value: 'Free',
			unit: null,
		}
	return {
		value: props.selected.price,
		unit: '€',
	}
})
</script>

<style scoped lang="scss">
.purchase-summary {
	--card-bg: var(--p-surface-900);
	--border: var(--p-surface-800);
	--muted: var(--p-surface-400);

	background: var(--card-bg);
	border: 1px solid var(--border);
	border-radius: 12px;
	padding: 16px;

	.purchase-summary__title {
		font-size: 22px;
		font-weight: 800;
		margin-bottom: 14px;
	}

	.purchase-summary__divider {
		border: none;
		height: 1px;
		background: var(--border);
		margin: 12px 0;
	}

	.purchase-summary__button {
		margin-top: 10px;
		width: 100%;
		background: var(--p-yellow-500);
		border-color: var(--p-yellow-500);
	}

	.purchase-summary__secure {
		margin-top: 12px;
		text-align: center;
		color: var(--muted);
		font-size: 13px;
	}
}
</style>
